// https://stackoverflow.com/questions/27078285/simple-throttle-in-js
/**
 * Rate-limit a function to at most once per `wait` ms.
 * @param {number} wait - Milliseconds between invocations
 * @param {Function} func - Function to throttle
 * @param {{leading?: boolean, trailing?: boolean}} [options]
 * @returns {Function}
 */
export function throttle(wait, func, options) {
    var context, args, result;
    var timeout = null;
    var previous = 0;
    if (!options) options = {};
    var later = function () {
        previous = options.leading === false ? 0 : Date.now();
        timeout = null;
        result = func.apply(context, args);
        if (!timeout) context = args = null;
    };
    return function () {
        var now = Date.now();
        if (!previous && options.leading === false) previous = now;
        var remaining = wait - (now - previous);
        context = this;
        args = arguments;
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            result = func.apply(context, args);
            if (!timeout) context = args = null;
        } else if (!timeout && options.trailing !== false) {
            timeout = setTimeout(later, remaining);
        }
        return result;
    };
}

/**
 * Delay function execution until `wait` ms after the last call.
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @param {boolean} [immediate] - Invoke on the leading edge instead of trailing
 * @returns {Function}
 */
export function debounce(func, wait, immediate) {
    var timeout;
    return function () {
        var context = this, args = arguments;
        var later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

/**
 * Returns true if the element's bottom edge is within the viewport.
 * @param {Element} el
 * @returns {boolean}
 */
export function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
}

/**
 * Returns true if the keyboard event is the Escape key.
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
export function isEscape(e) {
    // keyCode is getting deprecated use key instead when supported, which makes this messy
    var evt = e || window.event;
    var isEscape = false;
    if ("key" in evt) {
        isEscape = (evt.key == "Escape" || evt.key == "Esc");
    } else {
        isEscape = (evt.keyCode == 27);
    }
    return isEscape;
}

/**
 * Trap keyboard focus within `trap`. Adds a keydown listener — call once per open.
 * @param {Element} trap - Container to trap focus within
 * @param {boolean} [focusFirst=true] - Move focus to the first focusable element on call
 * @returns {Function} Function to remove the event listener when focus trap is no longer needed
 */
export function trapFocus(trap, focusFirst = true) {
    const focusableElements = 'button:not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"]), iframe';
    const firstFocusableElement = trap.querySelectorAll(focusableElements)[0]; // get first element to be focused inside trap
    const focusableContent = trap.querySelectorAll(focusableElements);
    const lastFocusableElement = focusableContent[focusableContent.length - 1]; // get last element to be focused inside trap
    const onKeyDown = function (e) {
        let isTabPressed = e.key === 'Tab' || e.keyCode === 9;

        if (!isTabPressed) {
            return;
        }

        if (e.shiftKey) { // if shift key pressed for shift + tab combination
            if (document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus(); // add focus for the last focusable element
                e.preventDefault();
            }
        } else { // if tab key is pressed
            if (document.activeElement === lastFocusableElement) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
                firstFocusableElement.focus(); // add focus for the first focusable element
                e.preventDefault();
            }
        }
    };
    document.addEventListener('keydown', onKeyDown);
    if (focusFirst) {
        setTimeout(() => {
            firstFocusableElement.focus();
        }, 350);
    }
    return () => document.removeEventListener('keydown', onKeyDown);
}

/**
 * Format a cents integer as a dollar string, e.g. 1999 → "$19.99".
 * @param {number} cents
 * @returns {string}
 */
export function formatMoney(cents) {
    const amount = cents / 100;
    return amount % 1 === 0
        ? `$${amount}`
        : `$${amount.toFixed(2)}`;
}

/**
 * Returns a price string, including ADA-friendly sale markup when
 * compareAtPrice is greater than price.
 */
export function formatPrice(price, compareAtPrice = null) {
    if (compareAtPrice && compareAtPrice > price) {
        return `<s aria-hidden="true">${formatMoney(compareAtPrice)}</s> <span class="visually-hidden">Original price: ${formatMoney(compareAtPrice)}</span>${formatMoney(price)}`;
    }
    return formatMoney(price);
}

/**
 * Escape HTML special characters to prevent XSS in innerHTML contexts.
 * @param {string} str
 * @returns {string}
 */
export function escapeHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// ---------------------------------------------------------------------------
// Loading states
// ---------------------------------------------------------------------------
/**
 * Show loading state on element
 * @param {HTMLElement} element - Element to show loading on
 */
export function addWorking(element) {
    element.classList.add('working');
}

/**
 * Hide loading state on element
 * @param {HTMLElement} element - Element to hide loading on
 */
export function removeWorking(element) {
    element.classList.remove('working');
}
